import type { DeliveryIntegrationConfig } from "@/lib/deliveries/config"

export const NOVA_POST_PRODUCTION_REST_URL = "https://api.novapost.com/v.1.0/"
export const NOVA_POST_UA_REST_URL = "https://api.novaposhta.ua/v.1.0/"
export const NOVA_POST_SANDBOX_REST_URL = "https://api-stage.novapost.pl/v.1.0/"
export const NOVA_POST_SANDBOX_ALTERNATIVE_REST_URL = "https://api-stage.novapost.com/v.1.0/"
export const NOVA_POST_UA_DIRECTORY_URL = "https://api.novaposhta.ua/v2.0/json/"

const NOVA_POST_REST_URLS = [
  NOVA_POST_PRODUCTION_REST_URL,
  NOVA_POST_UA_REST_URL,
  NOVA_POST_SANDBOX_REST_URL,
  NOVA_POST_SANDBOX_ALTERNATIVE_REST_URL,
] as const

export type NovaPostProviderConfigInput = {
  environment: "production" | "sandbox"
  baseUrl: (typeof NOVA_POST_REST_URLS)[number]
  directoryBaseUrl: typeof NOVA_POST_UA_DIRECTORY_URL
}

const NOVA_POST_UKRAINIAN_SETTLEMENT_QUERY =
  /^(?=.*[А-ЯІЇЄҐа-яіїєґ])[А-ЯІЇЄҐа-яіїєґ0-9 .,'’ʼ()/-]+$/u
const NOVA_POST_UKRAINIAN_LOCATION_DETAIL_QUERY =
  /^(?=.*(?:[А-ЯІЇЄҐа-яіїєґ]|\d))[А-ЯІЇЄҐа-яіїєґ0-9 .,'’ʼ()/#-]+$/u
const NOVA_POST_UKRAINIAN_ADDRESS_TEXT =
  /^(?=.*[А-ЯІЇЄҐа-яіїєґ])[А-ЯІЇЄҐа-яіїєґ0-9 .,'’ʼ()/#№-]+$/u

const NOVA_POST_SETTLEMENT_ALIASES: Readonly<Record<string, string>> = {
  "cherkasy": "Черкаси",
  "chernihiv": "Чернігів",
  "chernivtsi": "Чернівці",
  "dnipro": "Дніпро",
  "donetsk": "Донецьк",
  "ivano frankivsk": "Івано-Франківськ",
  "kharkiv": "Харків",
  "kherson": "Херсон",
  "khmelnytskyi": "Хмельницький",
  "kiev": "Київ",
  "kyiv": "Київ",
  "kropyvnytskyi": "Кропивницький",
  "luhansk": "Луганськ",
  "lutsk": "Луцьк",
  "lviv": "Львів",
  "mariupol": "Маріуполь",
  "mykolaiv": "Миколаїв",
  "odesa": "Одеса",
  "odessa": "Одеса",
  "poltava": "Полтава",
  "rivne": "Рівне",
  "sumy": "Суми",
  "ternopil": "Тернопіль",
  "uzhhorod": "Ужгород",
  "vinnytsia": "Вінниця",
  "zaporizhia": "Запоріжжя",
  "zaporizhzhia": "Запоріжжя",
  "zhytomyr": "Житомир",
}

export function isNovaPostUkrainianSettlementQuery(value: string): boolean {
  const normalized = value.trim()
  return normalized.length >= 2
    && NOVA_POST_UKRAINIAN_SETTLEMENT_QUERY.test(normalized)
}

export function normalizeNovaPostUkrainianSettlementQuery(
  value: string,
): string | null {
  const normalized = value.trim()
  if (isNovaPostUkrainianSettlementQuery(normalized)) return normalized
  return NOVA_POST_SETTLEMENT_ALIASES[normalizeSettlementAlias(normalized)] ?? null
}

/** Separate administrative hints from the carrier's settlement-name search. */
export function normalizeNovaPostSettlementSearch(value: string, region?: string): {
  settlementQuery: string
  regionQuery?: string
} | null {
  const parts = value.split(",").map(part => part.trim()).filter(Boolean)
  const name = (parts[0] ?? "").replace(/^(?:місто|м\.|село|с\.|селище|смт\.?)\s+/iu, "")
  const settlementQuery = normalizeNovaPostUkrainianSettlementQuery(name)
  const regionQuery = region?.trim() || parts.slice(1).find(part => /(?:область|обл\.?)(?:\s|$)/iu.test(part))
  if (!settlementQuery || (regionQuery && !isNovaPostUkrainianSettlementQuery(regionQuery))) return null
  return { settlementQuery, ...(regionQuery ? { regionQuery } : {}) }
}

export function isNovaPostUkrainianLocationDetailQuery(
  value: string,
): boolean {
  const normalized = value.trim()
  return normalized.length >= 1
    && NOVA_POST_UKRAINIAN_LOCATION_DETAIL_QUERY.test(normalized)
}

export function normalizeNovaPostUkrainianLocationDetailQuery(
  value: string,
): string | null {
  const normalized = stripNovaPostLocationLabels(value)
  if (isNovaPostUkrainianLocationDetailQuery(normalized)) return normalized

  const numberedLocation = normalized.match(
    /^(?:branch|warehouse|post office|pickup point|locker|parcel locker|postomat)\s*(?:no\.?|number|#|№)?\s*(\d+)$/i,
  )
  return numberedLocation?.[1] ?? null
}

/** Keep location type/carrier labels out of free-text search; preserve address words. */
export function stripNovaPostLocationLabels(value: string): string {
  const normalized = value.replace(/№/g, " ").normalize("NFKC")
    .replace(/["“”«»]/gu, " ")
    .replace(/\s+/g, " ").trim()
    .replace(/^(?:(?:поштомат(?:у|ом|і|а)?|почтомат|постамат|постомат|відділення|отделение|parcel locker|postomat|poshtomat|locker|branch|warehouse|post office|pickup point|пункт видачі|пункт прийому|нова пошта|новая почта|nova poshta|nova post|номер|number|no)(?:[\s:#.]+|$))+/iu, "")
    .trim()
  // Speech transcription can split a location number; never collapse address parts.
  if (/^\d[\d\s\p{Dash_Punctuation}\u2212]*$/u.test(normalized)) {
    return normalized.replace(/\D/g, "")
  }
  return normalized
}

export function isNovaPostUkrainianAddressText(value: string): boolean {
  const normalized = value.trim()
  return normalized.length >= 1
    && NOVA_POST_UKRAINIAN_ADDRESS_TEXT.test(normalized)
}

export function normalizeNovaPostProviderConfigInput(
  config: DeliveryIntegrationConfig["providerConfig"],
): NovaPostProviderConfigInput {
  return {
    environment: config.environment,
    baseUrl: novaPostRestUrl(config.baseUrl),
    directoryBaseUrl: NOVA_POST_UA_DIRECTORY_URL,
  }
}

export function createNovaPostIntegrationConfig(): DeliveryIntegrationConfig {
  return {
    version: 1,
    providerConfig: {
      environment: "production",
      baseUrl: NOVA_POST_UA_REST_URL,
      directoryBaseUrl: NOVA_POST_UA_DIRECTORY_URL,
    },
    sender: {
      name: "",
      phone: "",
      countryCode: "UA",
      location: {
        type: "branch",
        countryCode: "UA",
        providerLocationId: "",
      },
    },
    defaults: { deliveryPaymentPayer: "sender" },
  }
}

export function normalizeNovaPostIntegrationFormConfig(
  config: DeliveryIntegrationConfig,
): DeliveryIntegrationConfig {
  const location = config.sender.location.type === "address"
    ? config.sender.location
    : {
        ...config.sender.location,
        providerLocationCode:
          config.sender.location.providerLocationCode?.trim() || undefined,
      }
  return {
    ...config,
    sender: {
      ...config.sender,
      providerIdentity: config.sender.providerIdentity
        ? {
            partyReference:
              config.sender.providerIdentity.partyReference.trim(),
            contactReference:
              config.sender.providerIdentity.contactReference.trim(),
          }
        : undefined,
      location,
    },
    defaults: {
      deliveryPaymentPayer: config.defaults.deliveryPaymentPayer,
      payerContractReference:
        config.defaults.payerContractReference?.trim() || undefined,
      cashOnDeliverySettlementProfileReference:
        config.defaults.cashOnDeliverySettlementProfileReference?.trim()
        || undefined,
    },
  }
}

function novaPostRestUrl(
  value: string | undefined,
): NovaPostProviderConfigInput["baseUrl"] {
  const candidate = value ?? NOVA_POST_UA_REST_URL
  if (isNovaPostRestUrl(candidate)) return candidate
  throw new Error("Nova Post REST URL is not supported")
}

function normalizeSettlementAlias(value: string): string {
  return value.toLocaleLowerCase("en")
    .replace(/[.’'ʼ]/g, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function isNovaPostRestUrl(
  value: string,
): value is NovaPostProviderConfigInput["baseUrl"] {
  return NOVA_POST_REST_URLS.some(candidate => candidate === value)
}
